import { useState } from 'react'
import { useForm } from 'react-hook-form'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import { X, Mail, MessageSquare } from 'lucide-react'
import toast from 'react-hot-toast'
import { contactusAPI } from '../../services/contactusAPI'

export default function ContactSalesModal({ isOpen, onClose, plan, organizationId }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: ''
    }
  })

  const handleFormSubmit = async (data) => {
    setIsSubmitting(true)
    
    try {
      const customPrice = plan?.customPrice
      
      // Prepare contact data
      // This is for Enterprise plan inquiries, so use 'product_contact_us' flow
      const contactData = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message,
        planName: plan?.name || 'Enterprise',
        flowType: 'product_contact_us', // Enterprise inquiries go to sales team
        ...(organizationId && { organizationId }),
        ...(customPrice && customPrice > 0 && { customPrice })
      }

      // Submit via API
      const response = await contactusAPI.submit(contactData)
      
      if (response.data.success) {
        toast.success('Your inquiry has been sent successfully! Our sales team will contact you soon.')
        reset()
        onClose()
      } else {
        toast.error(response.data.message || 'Failed to send your inquiry. Please try again.')
      }
    } catch (error) {
      console.error('Error submitting contact form:', error)
      const errorMessage = error.response?.data?.message || 'Failed to send your inquiry. Please try again later.'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-xl font-semibold flex items-center">
            <Mail className="h-5 w-5 mr-2" />
            Contact Us
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
            <p className="text-sm text-blue-900">
              Interested in the <strong>{plan?.name || 'Enterprise'}</strong> plan? Our sales team will contact you to discuss custom pricing options tailored to your organization's needs.
            </p>
            {plan?.customPrice && plan.customPrice > 0 && (
              <p className="text-sm text-blue-800 mt-2 font-medium">
                Suggested Price: ${plan.customPrice.toLocaleString()}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Full Name <span className="text-red-500">*</span>
              </label>
              <Input
                {...register('name', { 
                  required: 'Full name is required',
                  minLength: {
                    value: 2,
                    message: 'Full name must be at least 2 characters'
                  }
                })}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Email Address <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <Input
                type="tel"
                {...register('phone', { 
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[\+]?[0-9\s\-\(\)]{10,20}$/,
                    message: 'Invalid phone number format. Please enter a valid phone number (10-20 digits)'
                  },
                  validate: (value) => {
                    // Remove all non-digit characters to count digits
                    const digitsOnly = value.replace(/\D/g, '');
                    if (digitsOnly.length < 10) {
                      return 'Phone number must contain at least 10 digits';
                    }
                    if (digitsOnly.length > 15) {
                      return 'Phone number cannot exceed 15 digits';
                    }
                    return true;
                  }
                })}
                placeholder="Enter your phone number (e.g., +1234567890, (123) 456-7890)"
              />
              {errors.phone && (
                <p className="text-sm text-red-500">{errors.phone.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('message', { 
                  required: 'Message is required',
                  minLength: {
                    value: 10,
                    message: 'Message must be at least 10 characters'
                  }
                })}
                rows={4}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Tell us about your requirements..."
              />
              {errors.message && (
                <p className="text-sm text-red-500">{errors.message.message}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                <Mail className="h-4 w-4 mr-2" />
                {isSubmitting ? 'Sending...' : 'Send Inquiry'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

